import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Building2, Globe, MapPin, Users, Pencil, Trash2,
  StickyNote, Clock, Pin,
} from 'lucide-react';
import { useOrganization, useUpdateOrganization, useDeleteOrganization } from '@/hooks/useOrganizations';
import { useNotes, useCreateNote, useDeleteNote, useToggleNotePin } from '@/hooks/useNotes';
import { useActivities } from '@/hooks/useActivities';
import {
  Button,
  Card,
  Tabs,
  Modal,
  ConfirmDialog,
  Input,
  Select,
  Textarea,
  Skeleton,
} from '@solid/ui';
import { Badge } from '@/components/ui/Badge';
import { CustomFieldsDisplay } from '@/components/CustomFieldsDisplay';
import { CustomFieldsForm } from '@/components/CustomFieldsForm';
import { formatDate, formatRelativeTime } from '@/lib/utils';
import type { OrgSize } from '@/types';

const sizeLabels: Record<OrgSize, string> = {
  SOLO: '1 person',
  SMALL: '2-10',
  MEDIUM: '11-50',
  LARGE: '51-200',
  ENTERPRISE: '200+',
};

export default function OrganizationDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: org, isLoading } = useOrganization(id);
  const [tab, setTab] = useState('overview');
  const [showEdit, setShowEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);

  const deleteOrg = useDeleteOrganization();

  const handleDelete = async () => {
    if (!id) return;
    await deleteOrg.mutateAsync(id);
    navigate('/admin/organizations');
  };

  if (isLoading) {
    return (
      <div className="page-enter">
        <div className="space-y-4">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-40 rounded-xl" />
        </div>
      </div>
    );
  }

  if (!org) return null;

  const location = [org.city, org.state, org.country].filter(Boolean).join(', ');

  return (
    <div className="page-enter">
      <div className="mb-6">
        <Link to="/admin/organizations" className="inline-flex items-center gap-1 text-sm text-grey-500 hover:text-secondary mb-4">
          <ArrowLeft className="w-4 h-4" />
          Back to Organizations
        </Link>

        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-heading-1 text-secondary flex items-center gap-3">
              <Building2 className="w-8 h-8 text-grey-400" />
              {org.name}
            </h1>
            <div className="flex items-center gap-3 mt-2">
              {org.industry && <span className="text-body-sm text-grey-500">{org.industry}</span>}
              {org.size && (
                <Badge variant="outline">
                  <Users className="w-3 h-3 mr-1" />
                  {sizeLabels[org.size]}
                </Badge>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setShowEdit(true)}>
              <Pencil className="w-3.5 h-3.5" />
              Edit
            </Button>
            <Button variant="destructive" size="sm" onClick={() => setShowDelete(true)} aria-label="Delete organization">
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Sidebar */}
        <div className="space-y-4">
          <Card padding="md">
            <h3 className="section-header mb-4">Organization Info</h3>
            <div className="space-y-3">
              {org.domain && (
                <div className="flex items-center gap-2 text-sm">
                  <Globe className="w-4 h-4 text-grey-400" />
                  <span className="text-grey-600">{org.domain}</span>
                </div>
              )}
              {org.website && (
                <div className="flex items-center gap-2 text-sm">
                  <Globe className="w-4 h-4 text-grey-400" />
                  <a href={org.website} target="_blank" rel="noopener noreferrer" className="link">
                    {org.website}
                  </a>
                </div>
              )}
              {location && (
                <div className="flex items-center gap-2 text-sm">
                  <MapPin className="w-4 h-4 text-grey-400" />
                  <span className="text-grey-600">{location}</span>
                </div>
              )}
            </div>
          </Card>

          <Card padding="md">
            <h3 className="section-header mb-2">Created</h3>
            <p className="text-body-sm text-grey-500">{formatDate(org.createdAt)}</p>
          </Card>

          <Card padding="md">
            <h3 className="section-header mb-3">Custom Fields</h3>
            <CustomFieldsDisplay entityType="ORGANIZATION" customFields={org.customFields} />
          </Card>
        </div>

        {/* Main content */}
        <div className="lg:col-span-2">
          <Tabs
            value={tab}
            onValueChange={setTab}
            items={[
              {
                value: 'overview',
                label: 'Overview',
                content: (
                  <div className="space-y-4">
                    <Card padding="md">
                      <h3 className="section-header mb-4">Deals</h3>
                      {org.deals?.length ? (
                        <div className="space-y-2">
                          {org.deals.map((deal) => (
                            <Link
                              key={deal.id}
                              to={`/admin/deals/${deal.id}`}
                              className="block p-3 rounded-lg hover:bg-grey-50 transition-colors"
                            >
                              <span className="font-medium text-secondary">{deal.title}</span>
                            </Link>
                          ))}
                        </div>
                      ) : (
                        <p className="section-subheader">No deals yet.</p>
                      )}
                    </Card>
                  </div>
                ),
              },
              {
                value: 'contacts',
                label: 'Contacts',
                content: (
                  <Card padding="md">
                    <h3 className="section-header mb-4">Contacts</h3>
                    {org.contacts?.length ? (
                      <div className="space-y-2">
                        {org.contacts.map((contact) => (
                          <Link
                            key={contact.id}
                            to={`/admin/contacts/${contact.id}`}
                            className="flex items-center justify-between p-3 rounded-lg hover:bg-grey-50 transition-colors"
                          >
                            <div>
                              <span className="font-medium text-secondary">
                                {contact.firstName} {contact.lastName}
                              </span>
                              {contact.jobTitle && (
                                <p className="text-xs text-grey-500">{contact.jobTitle}</p>
                              )}
                            </div>
                            {contact.email && (
                              <span className="text-sm text-grey-500">{contact.email}</span>
                            )}
                          </Link>
                        ))}
                      </div>
                    ) : (
                      <p className="section-subheader">No contacts yet.</p>
                    )}
                  </Card>
                ),
              },
              { value: 'activity', label: 'Activity', content: <OrgActivityTimeline organizationId={id!} /> },
              { value: 'notes', label: 'Notes', content: <OrgNotesSection organizationId={id!} /> },
            ]}
          />
        </div>
      </div>

      {showEdit && (
        <EditOrgDialog org={org} open={showEdit} onOpenChange={setShowEdit} />
      )}

      <ConfirmDialog
        open={showDelete}
        onOpenChange={setShowDelete}
        title="Delete organization?"
        description="Are you sure you want to delete this organization?"
        confirmLabel="Delete"
        destructive
        onConfirm={handleDelete}
      />
    </div>
  );
}

function OrgActivityTimeline({ organizationId }: { organizationId: string }) {
  const { data: activities, isLoading } = useActivities({ organizationId });

  if (isLoading) return <Skeleton className="h-32 rounded-xl" />;

  if (!activities?.length) {
    return (
      <Card padding="md">
        <div className="text-center py-8">
          <Clock className="w-8 h-8 text-grey-300 mx-auto mb-2" />
          <p className="text-body-sm text-grey-500">No activity yet.</p>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      {activities.map((activity) => (
        <Card key={activity.id} padding="sm">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm font-medium text-secondary">
                {activity.type.replace(/_/g, ' ')}
              </p>
              {activity.actor && <p className="text-xs text-grey-500">by {activity.actor.name}</p>}
            </div>
            <span className="text-xs text-grey-400">{formatRelativeTime(activity.createdAt)}</span>
          </div>
        </Card>
      ))}
    </div>
  );
}

function OrgNotesSection({ organizationId }: { organizationId: string }) {
  const { data: notes, isLoading } = useNotes({ organizationId });
  const createNote = useCreateNote();
  const deleteNote = useDeleteNote();
  const togglePin = useToggleNotePin();
  const [newNote, setNewNote] = useState('');

  const handleCreate = async () => {
    if (!newNote.trim()) return;
    await createNote.mutateAsync({ content: newNote, organizationId });
    setNewNote('');
  };

  return (
    <div className="space-y-4">
      <Card padding="md">
        <Textarea
          placeholder="Add a note..."
          value={newNote}
          onChange={(e) => setNewNote(e.target.value)}
          className="mb-3"
        />
        <div className="flex justify-end">
          <Button size="sm" onClick={handleCreate} loading={createNote.isPending} disabled={!newNote.trim()}>
            <StickyNote className="w-3.5 h-3.5" />
            Add Note
          </Button>
        </div>
      </Card>

      {isLoading ? (
        <Skeleton className="h-32 rounded-xl" />
      ) : !notes?.length ? (
        <Card padding="md">
          <div className="text-center py-8">
            <StickyNote className="w-8 h-8 text-grey-300 mx-auto mb-2" />
            <p className="text-body-sm text-grey-500">No notes yet.</p>
          </div>
        </Card>
      ) : (
        notes.map((note) => (
          <Card key={note.id} padding="sm">
            <div className="flex items-start justify-between mb-2">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-secondary">{note.author.name}</span>
                <span className="text-xs text-grey-400">{formatRelativeTime(note.createdAt)}</span>
                {note.isPinned && <Pin className="w-3 h-3 text-primary" />}
              </div>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => togglePin.mutate(note.id)}
                  className="p-1 rounded hover:bg-grey-100 text-grey-400 hover:text-grey-600"
                  aria-label={note.isPinned ? 'Unpin note' : 'Pin note'}
                >
                  <Pin className="w-3.5 h-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => deleteNote.mutate(note.id)}
                  className="p-1 rounded hover:bg-grey-100 text-grey-400 hover:text-error"
                  aria-label="Delete note"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
            <div className="text-sm text-grey-600 whitespace-pre-wrap">{note.content}</div>
          </Card>
        ))
      )}
    </div>
  );
}

function EditOrgDialog({
  org,
  open,
  onOpenChange,
}: {
  org: NonNullable<ReturnType<typeof useOrganization>['data']>;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [form, setForm] = useState({
    name: org.name,
    domain: org.domain || '',
    website: org.website || '',
    industry: org.industry || '',
    size: org.size || '',
    city: org.city || '',
    state: org.state || '',
  });
  const [customFields, setCustomFields] = useState<Record<string, any>>(org.customFields || {});
  const updateOrg = useUpdateOrganization();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await updateOrg.mutateAsync({
      id: org.id,
      data: {
        name: form.name,
        domain: form.domain || undefined,
        website: form.website || undefined,
        industry: form.industry || undefined,
        size: form.size || undefined,
        city: form.city || undefined,
        state: form.state || undefined,
        customFields,
      },
    });
    onOpenChange(false);
  };

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="Edit Organization"
      footer={
        <>
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="submit" form="edit-organization-form" loading={updateOrg.isPending}>
            Save Changes
          </Button>
        </>
      }
    >
      <form id="edit-organization-form" onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Name"
          required
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
        />
        <div className="grid grid-cols-2 gap-4">
          <Input
            label="Domain"
            value={form.domain}
            onChange={(e) => setForm({ ...form, domain: e.target.value })}
          />
          <Input
            label="Website"
            value={form.website}
            onChange={(e) => setForm({ ...form, website: e.target.value })}
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Input
            label="Industry"
            value={form.industry}
            onChange={(e) => setForm({ ...form, industry: e.target.value })}
          />
          <Select
            label="Company Size"
            value={form.size}
            onChange={(e) => setForm({ ...form, size: e.target.value })}
            options={[
              { value: '', label: 'Select size' },
              { value: 'SOLO', label: 'Solo (1)' },
              { value: 'SMALL', label: 'Small (2-10)' },
              { value: 'MEDIUM', label: 'Medium (11-50)' },
              { value: 'LARGE', label: 'Large (51-200)' },
              { value: 'ENTERPRISE', label: 'Enterprise (200+)' },
            ]}
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Input
            label="City"
            value={form.city}
            onChange={(e) => setForm({ ...form, city: e.target.value })}
          />
          <Input
            label="State"
            value={form.state}
            onChange={(e) => setForm({ ...form, state: e.target.value })}
          />
        </div>
        <CustomFieldsForm
          entityType="ORGANIZATION"
          currentValues={customFields}
          onChange={setCustomFields}
        />
      </form>
    </Modal>
  );
}
